import React, { useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { HeatmapChart } from "@carbon/charts-react";
import { Modal } from "@carbon/react";
import { useTranslation } from "react-i18next";
import ChartPanelWrapper from "../ChartPanelWrapper";
import { selectFilterConfig } from "../../../../features/dashboard/dashboardSlice";
import { dashboardDataRequestThunk } from "../../../../features/dashboard/dashboardThunks";
import { selectCameraFilter, selectDashboardHasCameraFilter, selectHeatmapOptions } from "../../../../features/dashboard/heatmapSelectors";
import { transformHeatmapData } from "./heatmapUtils";
import HeatmapCameraSelector from "./cameraSelector";
import "./_index.scss"

const Index = ({
  chart,
  overlayColor = "rgba(150, 151, 255, 0.3)",
  backgroundImageUrl = null,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [thumbnailModalOpen, setThumbnailModalOpen] = useState(false);
  const [selectedCamera, setSelectedCamera] = useState('');

  const filterConfig = useSelector(selectFilterConfig);
  const cameraFilter = useSelector(selectCameraFilter);
  const dashboardHasCameraFilter = useSelector(selectDashboardHasCameraFilter);
  const chartOptions = useSelector((state) => selectHeatmapOptions(state, chart));

  const camSelectorOptions = useMemo(() => cameraFilter?.options || [], [cameraFilter]);
  const camSelectorDisabled = camSelectorOptions.length === 0;

  useEffect(() => {
    if (dashboardHasCameraFilter) {
      setSelectedCamera('');
    }
  }, [dashboardHasCameraFilter]);

  useEffect(() => {
    if (!dashboardHasCameraFilter && camSelectorOptions.length > 0 && !selectedCamera) {
      setSelectedCamera(camSelectorOptions[0]?.value || '');
    }
  }, [camSelectorOptions, selectedCamera, dashboardHasCameraFilter]);

  const effectiveCamera = useMemo(() => {
    if (dashboardHasCameraFilter) return null;
    if (!cameraFilter || !selectedCamera) return null;
    const nullLabel = cameraFilter.allow_null ? cameraFilter.null_label : undefined;
    if (selectedCamera === nullLabel || selectedCamera === 'all') return null;
    return selectedCamera;
  }, [cameraFilter, selectedCamera, dashboardHasCameraFilter]);

  const onCameraChange = (val) => {
    if (val === selectedCamera) return;

    setSelectedCamera(val);
    if (cameraFilter) {
      dispatch(
        dashboardDataRequestThunk({
          dashboardId: chart.dashboard,
          panel: chart,
          filterConfig,
          filterValues: { [cameraFilter.id]: val }
        })
      ).catch((err) => {
        console.error(`Failed to fetch heatmap data with camera filter:`, err);
      });
    }
  };

  const cameraImageUrl = backgroundImageUrl || null;
  const heatmapStyle = cameraImageUrl ? {
    backgroundImage: `linear-gradient(0deg, ${overlayColor}, ${overlayColor}), url('${cameraImageUrl}')`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat'
  } : {};

  return (
    <ChartPanelWrapper chart={chart}>
      {(panelData, renderChart) => {
        const transformedData = transformHeatmapData(panelData, effectiveCamera, cameraFilter, chart);

        return renderChart(transformedData, (
          <div>
            <div className="heatmap-camera-bar">
              <h3 className="heatmap-camera-bar__title">
                {chartOptions.title}
              </h3>

              {!dashboardHasCameraFilter && (
                <HeatmapCameraSelector
                  options={camSelectorOptions}
                  value={selectedCamera}
                  disabled={camSelectorDisabled}
                  onChange={onCameraChange}
                />
              )}
            </div>

            <div className="heatmap-chart-wrapper" style={heatmapStyle}>
              <HeatmapChart options={chartOptions} data={transformedData} />
            </div>

            {cameraImageUrl && (
              <div className="heatmap-thumbnail-container">
                <button
                  type="button"
                  className="heatmap-thumbnail"
                  onClick={() => setThumbnailModalOpen(true)}
                  aria-label={t("heatmap.viewFullImage")}
                >
                  <img src={cameraImageUrl} alt={t("heatmap.cameraView")} />
                  <span className="heatmap-thumbnail-label">{t("heatmap.viewFullImage")}</span>
                </button>
              </div>
            )}

            <Modal
              open={thumbnailModalOpen}
              onRequestClose={() => setThumbnailModalOpen(false)}
              modalHeading={t("heatmap.cameraView")}
              passiveModal
              size="lg"
            >
              <div className="heatmap-modal-image">
                <img src={cameraImageUrl} alt={t("heatmap.fullCameraView")} />
              </div>
            </Modal>
          </div>
        ));
      }}
    </ChartPanelWrapper>
  );
};

export default React.memo(Index);
